import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Egg, TrendingUp, Calendar } from "lucide-react";

interface EggProductionStats {
  totalEggs: number;
  averagePerDay: number;
  lastProduction: string | null;
}

interface EggProductionStatsProps {
  stockItemId: string;
}

export const EggProductionStats = ({ stockItemId }: EggProductionStatsProps) => {
  const [stats, setStats] = useState<EggProductionStats>({
    totalEggs: 0,
    averagePerDay: 0,
    lastProduction: null,
  });

  useEffect(() => {
    const fetchStats = async () => {
      const { data, error } = await supabase
        .from('egg_productions')
        .select('quantity, production_date')
        .eq('stock_item_id', stockItemId);

      if (error) {
        console.error("Erreur lors de la récupération des statistiques:", error);
        return;
      }

      if (data) {
        const total = data.reduce((sum, record) => sum + record.quantity, 0);
        const dates = data.map(record => new Date(record.production_date));
        const lastDate = dates.length > 0 ? new Date(Math.max(...dates.map(d => d.getTime()))) : null;
        
        // Calculer la moyenne par jour
        const average = dates.length > 0 ? total / dates.length : 0;

        setStats({
          totalEggs: total,
          averagePerDay: Math.round(average),
          lastProduction: lastDate ? lastDate.toISOString() : null,
        });
      }
    };

    fetchStats();
  }, [stockItemId]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      <Card>
        <CardContent className="flex items-center p-6">
          <Egg className="h-8 w-8 text-primary mr-4" />
          <div>
            <p className="text-sm text-muted-foreground">Total des œufs</p>
            <h3 className="text-2xl font-bold">{stats.totalEggs}</h3>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="flex items-center p-6">
          <TrendingUp className="h-8 w-8 text-primary mr-4" />
          <div>
            <p className="text-sm text-muted-foreground">Moyenne par jour</p>
            <h3 className="text-2xl font-bold">{stats.averagePerDay}</h3>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="flex items-center p-6">
          <Calendar className="h-8 w-8 text-primary mr-4" />
          <div>
            <p className="text-sm text-muted-foreground">Dernière production</p>
            <h3 className="text-lg font-bold">
              {stats.lastProduction 
                ? new Date(stats.lastProduction).toLocaleDateString()
                : "Aucune"}
            </h3>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};